// /recaps — public grid of co-authored exchange recaps.
//
// Anonymous-OK. Wrapped in MarketingShell because recaps are editorial public
// content, not authenticated app surface. Cursor-paginated via the
// `nextCursor` field returned from /api/recaps/public/list.

import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { MarketingShell } from '../../components/marketing/MarketingShell';
import { EmptyState, MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';

export const Route = createFileRoute('/recaps/')({
  component: RecapsIndex,
});

type RecapCardDto = {
  id: string;
  slug: string;
  title: string | null;
  bodyExcerpt: string;
  photoUrl: string | null;
  publishedAt: string | null;
  venue: {
    id: string;
    slug: string;
    name: string;
    city: string;
    country: string;
  } | null;
  artist: {
    userId: string;
    slug: string | null;
    displayName: string;
    city: string | null;
    country: string | null;
  } | null;
};

type ListResponse = {
  items: RecapCardDto[];
  nextCursor: string | null;
};

const PAGE_LIMIT = 24;

function RecapsIndex() {
  const [items, setItems] = useState<RecapCardDto[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Initial fetch.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOk(`/api/recaps/public/list?limit=${PAGE_LIMIT}`);
        const body = (await res.json()) as ListResponse;
        if (cancelled) return;
        setItems(body.items);
        setCursor(body.nextCursor);
        setErrMsg(null);
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e instanceof ApiError ? e.message : "Couldn't load recaps just now.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_LIMIT), cursor });
      const res = await fetchOk(`/api/recaps/public/list?${params.toString()}`);
      const body = (await res.json()) as ListResponse;
      setItems((prev) => (prev === null ? body.items : [...prev, ...body.items]));
      setCursor(body.nextCursor);
    } catch (e) {
      setErrMsg(e instanceof ApiError ? e.message : "Couldn't load more just now.");
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <MarketingShell>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          <MotionSection>
            <PageHero
              eyebrow="Stories"
              title="Recaps from the road"
              lede="Co-authored by the artist and the venue, after the night was done."
            />
          </MotionSection>

          {errMsg !== null ? (
            <Text c="red">{errMsg}</Text>
          ) : items === null ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : items.length === 0 ? (
            <EmptyState
              title="No recaps yet."
              body="The first published recaps will land here once an artist and a host co-author one."
            />
          ) : (
            <MotionSection>
              <Stack gap="lg">
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                  {items.map((r) => (
                    <RecapCard key={r.id} recap={r} />
                  ))}
                </SimpleGrid>
                {cursor !== null ? (
                  <Group justify="center">
                    <Button onClick={loadMore} loading={loadingMore} variant="default">
                      Load more
                    </Button>
                  </Group>
                ) : null}
              </Stack>
            </MotionSection>
          )}
        </Stack>
      </Container>
    </MarketingShell>
  );
}

function RecapCard({ recap }: { recap: RecapCardDto }) {
  const fallbackTitle =
    recap.artist !== null && recap.venue !== null
      ? `${recap.artist.displayName} at ${recap.venue.name}`
      : recap.venue?.name ?? recap.artist?.displayName ?? 'A night on the road';
  const titleText = recap.title !== null && recap.title.length > 0 ? recap.title : fallbackTitle;
  const dateLabel =
    recap.publishedAt !== null
      ? new Date(recap.publishedAt).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null;

  // The card itself is the click target (single nav). The artist credit shows
  // inside as plain text — visiting the artist's profile is one extra tap from
  // the recap detail page, which keeps this card's hit-target unambiguous.
  return (
    <Link
      to="/recaps/$slug"
      params={{ slug: recap.slug }}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
    >
      <Card
        withBorder
        radius="md"
        padding={0}
        h="100%"
        style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <Box style={{ aspectRatio: '16 / 10', position: 'relative' }}>
          {recap.photoUrl !== null ? (
            <img
              src={recap.photoUrl}
              alt={titleText}
              loading="lazy"
              decoding="async"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(135deg, var(--mantine-color-brand-4), var(--mantine-color-sun-3))',
              }}
            />
          )}
        </Box>
        <Stack gap="xs" p="md" style={{ flex: 1 }}>
          <Group gap="xs" wrap="wrap">
            <Badge variant="light" color="brand">
              Recap
            </Badge>
            {dateLabel !== null ? (
              <Text size="xs" c="dimmed">
                {dateLabel}
              </Text>
            ) : null}
          </Group>
          <Text fw={600} lineClamp={2} size="lg">
            {titleText}
          </Text>
          {recap.bodyExcerpt.length > 0 ? (
            <Text size="sm" c="ink.7" lineClamp={3}>
              {recap.bodyExcerpt}
            </Text>
          ) : null}
          <Group gap={4} mt="auto" wrap="wrap">
            {recap.artist !== null ? (
              <Text size="xs" c="dimmed">
                {recap.artist.displayName}
              </Text>
            ) : null}
            {recap.artist !== null && recap.venue !== null ? (
              <Text size="xs" c="dimmed">
                ·
              </Text>
            ) : null}
            {recap.venue !== null ? (
              <Text size="xs" c="dimmed">
                {recap.venue.name}, {recap.venue.city}
              </Text>
            ) : null}
          </Group>
        </Stack>
      </Card>
    </Link>
  );
}
